import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Loader2, ShoppingBag, Search } from "lucide-react";

import SidebarLayout from "@/components/SidebarLayout";
import UpsellBanner from "@/components/upsell/UpsellBanner";
import { AiAssetBadge } from "@/components/marketplace/AiAssetBadge";
import { AssetPricingBadge } from "@/components/marketplace/AssetPricingBadge";
import { MarketplaceTrendingCarousel } from "@/components/marketplace/MarketplaceTrendingCarousel";
import { MarketplaceNewCarousel } from "@/components/marketplace/MarketplaceNewCarousel";
import { MarketplaceTopCreatorsCarousel } from "@/components/marketplace/MarketplaceTopCreatorsCarousel";
import { MarketplaceCategoryChips } from "@/components/marketplace/MarketplaceCategoryChips";
import { VideoTrendingCarousel } from "@/components/videos/VideoTrendingCarousel";
import VideoPackCarousel from "@/components/videos/VideoPackCarousel";
import { useCart } from "@/context/CartContext";

const API = process.env.REACT_APP_BACKEND_URL || "";

async function fetchJson(path) {
  const r = await fetch(`${API}${path}`, { credentials: "include" });
  if (r.status === 503) return { __disabled: true };
  if (!r.ok) return null;
  return r.json();
}

const FILTERS = [
  { key: "all", label: "All" },
  { key: "paid", label: "Paid" },
  { key: "free", label: "Free" },
  { key: "ai", label: "AI Generated" },
];

const SORTS = [
  { value: "newest", label: "Newest" },
  { value: "bestsellers", label: "Best sellers" },
  { value: "price_asc", label: "Price ↑" },
  { value: "price_desc", label: "Price ↓" },
];

function seoInject(items, count) {
  try {
    // JSON-LD ItemList SEO block. Adds/replaces `#mp-jsonld` <script>.
    const el = document.getElementById("mp-jsonld") || document.createElement("script");
    el.id = "mp-jsonld";
    el.type = "application/ld+json";
    el.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Marketplace — Trending + New arrivals",
      description: `${count} digital assets available now`,
      numberOfItems: items.length,
      itemListElement: items.slice(0, 10).map((it, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": it.ai_generated || it.creator_type === "virtual"
            ? "SoftwareApplication"
            : "Product",
          name: it.title,
          description: (it.description || "").slice(0, 200),
          offers: {
            "@type": "Offer",
            priceCurrency: "USD",
            price: ((it.price_eur_cents || 0) / 100).toFixed(2),
            availability: "https://schema.org/InStock",
          },
        },
      })),
    });
    if (!el.parentNode) document.head.appendChild(el);
  } catch {}
}

export default function MarketplaceHub() {
  const cart = useCart();
  const [trending, setTrending] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [topCreators, setTopCreators] = useState([]);
  const [categories, setCategories] = useState([]);

  const [gridItems, setGridItems] = useState([]);
  const [gridTotal, setGridTotal] = useState(0);
  const [gridLoading, setGridLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("newest");
  const [activeCategory, setActiveCategory] = useState(null);
  const [query, setQuery] = useState("");
  const [disabled, setDisabled] = useState(false);

  // Initial load: 4 sections
  useEffect(() => {
    (async () => {
      const [tr, nw, tc, ca] = await Promise.all([
        fetchJson("/api/marketplace/trending?days=7&limit=8"),
        fetchJson("/api/marketplace/new?limit=8"),
        fetchJson("/api/marketplace/top-creators?days=30&limit=10"),
        fetchJson("/api/marketplace/categories"),
      ]);
      if (tr?.__disabled || nw?.__disabled) setDisabled(true);
      setTrending(tr?.items || []);
      setNewArrivals(nw?.items || []);
      setTopCreators(tc?.items || []);
      setCategories(ca?.items || []);
    })();
  }, []);

  // Grid load
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setGridLoading(true);
      const params = new URLSearchParams();
      if (filter === "ai") {
        // Use dedicated ai-assets endpoint
        const r = await fetchJson(`/api/marketplace/ai-assets?limit=48`);
        if (!cancelled) {
          if (r?.__disabled) setDisabled(true);
          setGridItems(r?.items || []);
          setGridTotal(r?.total || 0);
          setGridLoading(false);
        }
        return;
      }
      params.set("limit", "48");
      if (activeCategory) params.set("category", activeCategory);
      if (query.trim()) params.set("q", query.trim());
      const r = await fetchJson(`/api/marketplace/paid/list?${params.toString()}`);
      if (cancelled) return;
      if (r?.__disabled) setDisabled(true);
      let items = r?.items || [];
      if (filter === "free") items = items.filter((x) => !x.is_paid || !x.price_eur_cents);
      if (filter === "paid") items = items.filter((x) => x.is_paid && x.price_eur_cents > 0);
      // Sort client-side
      if (sort === "price_asc") items.sort((a, b) => (a.price_eur_cents || 0) - (b.price_eur_cents || 0));
      else if (sort === "price_desc") items.sort((a, b) => (b.price_eur_cents || 0) - (a.price_eur_cents || 0));
      else if (sort === "bestsellers") items.sort((a, b) => (b.sales_count || 0) - (a.sales_count || 0));
      setGridItems(items);
      setGridTotal(r?.total || items.length);
      setGridLoading(false);
    })();
    return () => { cancelled = true; };
  }, [filter, sort, activeCategory, query]);

  useEffect(() => {
    // Update SEO JSON-LD when trending/new refreshed
    seoInject([...trending, ...newArrivals], gridTotal);
    // Update dynamic title
    const suffix = activeCategory ? ` · ${activeCategory}` : filter === "ai" ? " · AI Generated" : "";
    document.title = `Marketplace${suffix} — ${gridTotal} assets`;
  }, [trending, newArrivals, gridTotal, activeCategory, filter]);

  const metaDesc = useMemo(
    () => `${gridTotal} digital assets — templates, scripts, carousels & AI-generated content. Bundle discount up to 35% off.`,
    [gridTotal],
  );

  return (
    <SidebarLayout>
      <div className="max-w-[1280px] w-full mx-auto p-6 md:p-8 space-y-8">
        {/* Dynamic SEO meta description */}
        <meta name="description" content={metaDesc} />

        <UpsellBanner surface="marketplace" />

        <motion.header initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-text-secondary mb-1">
              <ShoppingBag className="h-3 w-3 text-[var(--color-secondary)]" />
              Marketplace · V2 · Creator Economy
            </div>
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">
              Discover, buy, download
            </h1>
            <p className="text-xs text-text-secondary mt-1 max-w-xl">
              Curated packs · community creator drops · AI-generated content.
              Bundle 3 assets for <span className="text-[var(--color-secondary)]">20%</span> off · 5+ for
              <span className="text-[var(--color-secondary)]"> 35%</span>.
            </p>
          </div>
          <div className="relative w-full md:w-80">
            <Search className="h-4 w-4 text-text-secondary absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="mp-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              type="text"
              placeholder="Search assets…"
              className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] pl-10 pr-4 py-2 text-sm text-text-main placeholder:text-text-secondary/60 focus:outline-none focus:border-[var(--color-primary)]"
            />
          </div>
        </motion.header>

        {disabled && (
          <div
            data-testid="mp-disabled-banner"
            className="rounded-2xl border border-neutral-400/30 bg-neutral-400/5 p-6 text-sm text-neutral-200"
          >
            <span className="font-semibold">Marketplace paused</span> — MARKETPLACE_ENABLED is OFF.
            Contact admin to re-enable browsing and purchases.
          </div>
        )}

        {!disabled && trending.length > 0 && (
          <MarketplaceTrendingCarousel items={trending} loading={false} />
        )}
        {!disabled && <VideoTrendingCarousel />}
        {!disabled && <VideoPackCarousel />}
        {!disabled && newArrivals.length > 0 && (
          <MarketplaceNewCarousel items={newArrivals} loading={false} />
        )}
        {!disabled && topCreators.length > 0 && (
          <MarketplaceTopCreatorsCarousel items={topCreators} loading={false} />
        )}
        {!disabled && categories.length > 0 && (
          <MarketplaceCategoryChips
            items={categories}
            active={activeCategory}
            onSelect={setActiveCategory}
          />
        )}

        {/* Filter + Sort bar */}
        <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-1">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                data-testid={`mp-filter-${f.key}`}
                onClick={() => setFilter(f.key)}
                className={`text-xs px-3 py-1.5 rounded-full uppercase tracking-widest border transition-colors ${
                  filter === f.key
                    ? "border-[var(--color-primary)] bg-[var(--color-primary)]/15 text-[var(--color-primary)]"
                    : "border-[var(--color-border)] text-text-secondary hover:border-white/20"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2 text-xs text-text-secondary">
            <span>Sort:</span>
            <select
              data-testid="mp-sort-select"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="rounded-full border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-1.5 text-xs text-text-main focus:outline-none"
            >
              {SORTS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
        </section>

        {/* Grid */}
        <section>
          <div className="mb-3 text-xs text-text-secondary">
            <span data-testid="mp-grid-total">{gridItems.length}</span> of {gridTotal} results
            {activeCategory ? ` in "${activeCategory}"` : ""}
            {filter !== "all" ? ` (${filter})` : ""}
          </div>
          {gridLoading ? (
            <div className="flex items-center justify-center py-16 text-text-secondary text-xs">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
            </div>
          ) : gridItems.length === 0 ? (
            <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-16 text-center text-sm text-text-secondary">
              No assets match this filter.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {gridItems.map((it) => (
                <GridCard key={it.asset_id} item={it} cart={cart} />
              ))}
            </div>
          )}
        </section>
      </div>
    </SidebarLayout>
  );
}

function GridCard({ item, cart }) {
  const inCart = cart.has(item.asset_id);
  return (
    <div
      data-testid={`mp-grid-card-${item.asset_id}`}
      className="group rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4 hover:border-[var(--color-primary)]/60 transition-colors flex flex-col"
    >
      <Link to={`/marketplace/asset/${item.asset_id}`} className="flex-1">
        <div className="flex items-center justify-between mb-2">
          <AssetPricingBadge is_paid={item.is_paid} price_eur_cents={item.price_eur_cents} size="sm" />
          {(item.ai_generated || item.creator_type === "virtual") && (
            <AiAssetBadge assetAiType={item.asset_ai_type} />
          )}
        </div>
        <h3 className="text-sm font-semibold text-[var(--color-text-main)] line-clamp-2 group-hover:text-[var(--color-secondary)] transition-colors mb-2">
          {item.title}
        </h3>
        <p className="text-[11px] text-text-secondary line-clamp-2 leading-snug">
          {item.description || ""}
        </p>
      </Link>
      <button
        data-testid={`mp-grid-add-cart-${item.asset_id}`}
        onClick={(e) => { e.preventDefault(); cart.add(item.asset_id); }}
        disabled={inCart || !item.is_paid || !item.price_eur_cents}
        className={`mt-3 text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full font-semibold transition-colors ${
          inCart
            ? "bg-[var(--color-success)]/20 text-[var(--color-success)] border border-[var(--color-success)]/40 cursor-default"
            : !item.is_paid || !item.price_eur_cents
            ? "bg-white/5 text-text-secondary cursor-not-allowed border border-transparent"
            : "bg-white hover:bg-[#EDEDED] text-black hover:opacity-90"
        }`}
      >
        {inCart ? "✓ In cart" : !item.is_paid ? "Free" : "Add to cart"}
      </button>
    </div>
  );
}
